import { defineStore } from 'pinia';
import * as _ from 'lodash';

import * as backend from '../backend';
import { useGlobalMessageStore } from '@/plugins/clientModels/store/globalMessageStore';
import { PasswordUpdatePendingError, AuthenticationError } from '../errors';
import { GlobalMessage } from '@/plugins/clientModels/types';
import { router } from '@/router';
import type VueRouter from 'vue-router';

interface AuthStoreI {
	login: any;
	error: string | Error | null;
	loading: {
		authentication: boolean;
		passwordUpdate: boolean;
	};
	// requestedRoute: string | null;
}

export interface UserLoginRequestI {
	emailOrUsername?: string;
	password?: string;
}

export type ApiLoginRequestI =
	| {
			password: string;
			username?: string;
			email?: string;
	  }
	| undefined;

export interface UpdatePasswordRequestI {
	username?: string;
	email?: string;
	oldPassword: string;
	password: string;
	passwordCheck: string;
}

const state: AuthStoreI = {
	login: null,
	error: null,
	loading: {
		authentication: false,
		passwordUpdate: false,
	},
};

function displayError(error: any) {
	const globalMessageStore = useGlobalMessageStore();
	globalMessageStore.addMessage(new GlobalMessage({ severity: 'error', title: 'Fehler', message: error }));
}

async function performApiLogin(loginRequest: ApiLoginRequestI = undefined) {
	try {
		// ToDo: This is called twice on initial load, by mistake.
		console.log('trying to login');
		const login = await backend.login(loginRequest);
		return login;
	} catch (error) {
		if (_.get(error, 'response.data.code') === 'passwordUpdatePending') throw new PasswordUpdatePendingError();
		else if (_.get(error, 'response.status') === 401) throw new AuthenticationError();
		else throw error;
	}
}

export const useAuthStore = defineStore('auth', {
	state: () => state,
	getters: {
		isAdmin: (state) => _.get(state, 'login.effectiveFrontendRoles', []).includes('admin'),
		frontendRoles: (state) => {
			if (!state.login) return [];
			const groups = _.get(state.login, 'references.groups', []);
			return _.uniq(_.flatten(groups.map((group: any) => _.get(group, 'permissions.frontendRoles', []))));
		},
		loginWorker: (state) => _.get(state.login, 'references.worker', undefined),
	},
	actions: {
		async getLogin() {
			// fetch the user from state if possible
			if (this.login) return this.login;
			// otherwise, try to login via a session cookie, but do not show an error if it fails
			else return await this.optimisticallyLoginWithTokenOnly();
		},
		async optimisticallyLoginWithTokenOnly() {
			try {
				this.login = await performApiLogin();
				this.error = null;
				return this.login;
			} catch (error) {
				this.login = null;
				this.error = null;
			}
		},
		async loginAndStoreCredentials(loginRequest?: ApiLoginRequestI) {
			try {
				this.login = await performApiLogin(loginRequest);
				this.error = null;
				return this.login;
			} catch (error) {
				this.login = null;
				this.error = error as Error;
				throw error;
			}
		},
		async authenticate({ emailOrUsername, password }: UserLoginRequestI = {}) {
			this.loading.authentication = true;

			let loginRequest: ApiLoginRequestI = undefined;
			// Check if the username is a username or an email address and set it to the correct field.
			if (emailOrUsername && password) {
				loginRequest = { password };
				if (/.*[@].*/.test(emailOrUsername)) loginRequest.email = emailOrUsername;
				else loginRequest.username = emailOrUsername;
			}

			const nextRoute = _.get<VueRouter, string, string>(router, 'currentRoute.query.returnUrl', '/');
			try {
				await this.loginAndStoreCredentials(loginRequest);
				router.push(nextRoute);
			} catch (error) {
				this.loading.authentication = false;
				if (error instanceof PasswordUpdatePendingError) {
					router.push({
						path: '/updatePassword',
						query: { username: emailOrUsername, returnUrl: nextRoute },
					});
				} else if (error instanceof AuthenticationError) {
					this.error = 'Anmeldung fehlgeschlagen. Username oder Passwort inkorrekt.';
				} else {
					displayError(error);
				}
			}

			this.loading.authentication = false;
		},
		async logout() {
			try {
				await backend.logout();
				this.login = null;
				this.error = null;
				router.push('/login');
			} catch (error) {
				displayError(error);
			}
		},
		async updatePassword(updatePasswordRequest: UpdatePasswordRequestI) {
			this.loading.passwordUpdate = true;
			try {
				const login = await backend.updatePassword(updatePasswordRequest);
				if (login) {
					const nextRoute = _.get<VueRouter, string, string>(router, 'currentRoute.query.returnUrl', '/');
					router.push(nextRoute);
				} else {
					throw new Error('Could not update password.');
				}
			} catch (error) {
				displayError(error);
			}
			this.loading.passwordUpdate = false;
		},
	},
});
